import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

/* ============================================================
   HOTEL VENDOR DASHBOARD  (live data)
   Sources:
     GET /api/hotel-booking?page&limit     -> bookings (server.js me jo mount hai wahi path rakho)
     GET /api/vendor/hotels                -> hotels
     GET /api/hotel-rooms/hotel/:hotelId   -> rooms (har hotel ke liye)
     GET /api/hotel-inventory?limit=1      -> inventory set hai ya nahi (pagination.total)
   Props: onNavigate(tabId)  -> VendorLayout ka setActiveTab
============================================================ */

const BOOKINGS_API = "https://bmtvendorvendorfinal.onrender.com/api/hotel-booking";
const HOTELS_API = "https://bmtvendorvendorfinal.onrender.com/api/vendor/hotels";
const ROOMS_API = "https://bmtvendorvendorfinal.onrender.com/api/hotel-rooms";
const INVENTORY_API = "https://bmtvendorvendorfinal.onrender.com/api/hotel-inventory";

const PAGE_SIZE = 100; // backend max
const MAX_PAGES = 10; // 1000 bookings tak

const getToken = () => {
  const t = localStorage.getItem("vendorToken") || localStorage.getItem("token");
  return t && t !== "null" && t !== "undefined" ? t : null;
};
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const errText = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

/* tab ids (VendorLayout ke hotel panel se match) */
const GO = {
  bookings: "hotels_bookings",
  inventory: "hotel_room_inventory",
  property: "hotels_manage",
  rooms: "hotel_rooms",
};

/* stays jo "sold" count hoti hain / rooms rokte hain */
const SOLD = ["CONFIRMED", "CHECKED_IN", "CHECKED_OUT", "COMPLETED"];

/* ---------- date helpers (booking dates UTC midnight par hoti hain) ---------- */
const pad = (n) => String(n).padStart(2, "0");
const localKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};
const dayKey = (d) => new Date(d).toISOString().slice(0, 10);
const addDays = (key, n) => {
  const d = new Date(`${key}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const diffDays = (a, b) => Math.round((Date.parse(a) - Date.parse(b)) / 864e5);
const overlap = (b, from, to) => {
  const s = Math.max(Date.parse(dayKey(b.checkIn)), Date.parse(from));
  const e = Math.min(Date.parse(dayKey(b.checkOut)), Date.parse(to));
  return Math.max(Math.round((e - s) / 864e5), 0);
};
const weekday = (key) => new Date(`${key}T00:00:00.000Z`).toLocaleDateString("en-IN", { weekday: "short", timeZone: "UTC" });
const shortDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "UTC" });
const money = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const parseList = (d) =>
  Array.isArray(d) ? d
  : Array.isArray(d?.hotels) ? d.hotels
  : Array.isArray(d?.data) ? d.data
  : Array.isArray(d?.data?.hotels) ? d.data.hotels : [];

const hotelIdOf = (b) => (b.hotel && b.hotel._id) || b.hotel || "";

const STATUS_LABEL = {
  PENDING: ["Needs reply", "#b45309", "#fef3c7"],
  CONFIRMED: ["Confirmed", "#166534", "#dcfce7"],
  CHECKED_IN: ["Checked in", "#1d4ed8", "#dbeafe"],
};

async function fetchAllBookings() {
  const first = await axios.get(BOOKINGS_API, { ...authHeaders(), params: { page: 1, limit: PAGE_SIZE } });
  let all = first.data.bookings || [];
  const pages = Math.min(first.data.pages || 1, MAX_PAGES);
  for (let p = 2; p <= pages; p += 1) {
    const next = await axios.get(BOOKINGS_API, { ...authHeaders(), params: { page: p, limit: PAGE_SIZE } });
    all = all.concat(next.data.bookings || []);
  }
  return all;
}

export default function HotelDashboard({ onNavigate = () => {} }) {
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [inventoryTotal, setInventoryTotal] = useState(0);
  const [hotelId, setHotelId] = useState("");
  const [metric, setMetric] = useState("revenue"); // revenue | nights
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  const today = useMemo(() => localKey(new Date()), []);

  /* ---------- load ---------- */
  const load = useCallback(async () => {
    if (!getToken()) {
      window.location.href = "/vendor-login";
      return;
    }
    setLoading(true);
    const problems = [];

    let hotelList = [];
    try {
      const r = await axios.get(HOTELS_API, authHeaders());
      hotelList = parseList(r.data);
    } catch (e) {
      problems.push(errText(e, "Hotels load nahi hue"));
    }

    const [bk, rm, inv] = await Promise.all([
      fetchAllBookings().catch((e) => {
        problems.push(errText(e, "Bookings load nahi hui"));
        return [];
      }),
      Promise.all(
        hotelList.map((h) =>
          axios
            .get(`${ROOMS_API}/hotel/${h._id}`, authHeaders())
            .then((r) => (r.data.rooms || []).map((x) => ({ ...x, _hotelId: h._id })))
            .catch(() => [])
        )
      ),
      axios
        .get(INVENTORY_API, { ...authHeaders(), params: { limit: 1 } })
        .then((r) => r.data.pagination?.total || 0)
        .catch(() => 0),
    ]);

    setHotels(hotelList);
    setBookings(bk);
    setRooms(rm.flat());
    setInventoryTotal(inv);
    setIssues(problems);
    setUpdatedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- scope (hotel filter) ---------- */
  const scopedBookings = useMemo(
    () => (hotelId ? bookings.filter((b) => String(hotelIdOf(b)) === hotelId) : bookings),
    [bookings, hotelId]
  );
  const scopedRooms = useMemo(
    () => (hotelId ? rooms.filter((r) => r._hotelId === hotelId) : rooms),
    [rooms, hotelId]
  );

  /* ---------- stats ---------- */
  const stats = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6)); // last 7 days, aaj last me
    const nightsDay = Array(7).fill(0);
    const revDay = Array(7).fill(0);

    let ciToday = 0;
    let ci7 = 0;
    let newToday = 0;
    let newTodayAmt = 0;
    const count = { PENDING: 0, CONFIRMED: 0, CHECKED_IN: 0, CANCELLED: 0 };
    const arrivals = [];
    const departures = [];
    const upcoming = [];

    scopedBookings.forEach((b) => {
      const st = b.bookingStatus;
      const roomsN = Number(b.roomsBooked) || 1;
      const rate = Number(b.pricePerNight) || 0;
      const inKey = dayKey(b.checkIn);
      const outKey = dayKey(b.checkOut);

      if (st === "PENDING" || st === "CONFIRMED" || st === "CHECKED_IN") count[st] += 1;
      if (st === "CANCELLED" || st === "NO_SHOW") count.CANCELLED += 1;

      if (SOLD.includes(st)) {
        days.forEach((d, i) => {
          if (overlap(b, d, addDays(d, 1)) > 0) {
            nightsDay[i] += roomsN;
            revDay[i] += rate * roomsN;
          }
        });
        if (inKey >= days[0] && inKey <= today) {
          ci7 += 1;
          if (inKey === today) ciToday += 1;
        }
      }

      if (!["CANCELLED", "NO_SHOW"].includes(st) && b.createdAt && localKey(b.createdAt) === today) {
        newToday += 1;
        newTodayAmt += Number(b.totalAmount) || 0;
      }

      if (["PENDING", "CONFIRMED", "CHECKED_IN"].includes(st) && inKey === today) arrivals.push(b);
      if (st === "CHECKED_IN" && outKey <= today) departures.push(b);
      if (["PENDING", "CONFIRMED"].includes(st) && inKey >= today) upcoming.push(b);
    });

    upcoming.sort((a, b) => new Date(a.checkIn) - new Date(b.checkIn));

    const nights7 = nightsDay.reduce((s, x) => s + x, 0);
    const rev7 = revDay.reduce((s, x) => s + x, 0);
    const nightsToday = nightsDay[6];
    const revToday = revDay[6];

    const totalRooms = scopedRooms.reduce((s, r) => s + (r.isActive === false ? 0 : Number(r.totalRooms) || 0), 0);
    const occupancy = totalRooms > 0 ? Math.min(Math.round((nightsToday / totalRooms) * 100), 100) : null;

    return {
      days, nightsDay, revDay, nights7, rev7, nightsToday, revToday,
      adrToday: nightsToday ? revToday / nightsToday : 0,
      adr7: nights7 ? rev7 / nights7 : 0,
      ciToday, ci7, newToday, newTodayAmt, count,
      arrivals, departures, upcoming: upcoming.slice(0, 5),
      totalRooms, occupancy,
    };
  }, [scopedBookings, scopedRooms, today]);

  /* ---------- property setup checklist ---------- */
  const setup = useMemo(() => {
    const approved = hotels.some((h) => h.status === "approved");
    const allHaveImages = hotels.length > 0 && hotels.every((h) => (h.hotelImages || []).length > 0);
    return [
      { label: "Add your hotel", done: hotels.length > 0, go: GO.property },
      { label: "Upload hotel photos", done: allHaveImages, go: GO.property },
      {
        label: approved ? "Hotel approved" : "Wait for hotel approval",
        done: approved,
        go: GO.property,
        note: !approved && hotels.length > 0 ? "Under review" : "",
      },
      { label: "Add rooms and prices", done: rooms.length > 0, go: GO.rooms },
      { label: "Open dates in Rates & Inventory", done: inventoryTotal > 0, go: GO.inventory },
    ];
  }, [hotels, rooms, inventoryTotal]);
  const setupDone = setup.filter((s) => s.done).length;

  /* ---------- tiles ---------- */
  const tiles = [
    {
      title: "Today's Room Nights",
      value: stats.nightsToday,
      sub: `Last 7 days: ${plural(stats.nights7, "room night")}`,
      go: GO.bookings,
    },
    {
      title: "Today's Revenue",
      value: money(stats.revToday),
      sub: `Last 7 days: ${money(stats.rev7)} (room revenue, excl. tax)`,
      go: GO.bookings,
    },
    {
      title: "Today's Average Selling Price",
      value: money(stats.adrToday),
      sub: `Last 7 days: ${money(stats.adr7)}`,
      go: GO.inventory,
    },
    {
      title: "Today's Check-ins",
      value: stats.ciToday,
      sub: `Last 7 days: ${plural(stats.ci7, "check-in")}`,
      go: GO.bookings,
    },
  ];

  const chartValues = metric === "revenue" ? stats.revDay : stats.nightsDay;
  const chartMax = Math.max(...chartValues, 1);

  /* ============================================================ RENDER */
  return (
    <div style={S.page} className="hd-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes hdspin { to { transform: rotate(360deg); } }
        .hd-page button:focus-visible, .hd-page select:focus-visible { outline: 2px solid #166534; outline-offset: 2px; }
        .hd-tile, .hd-row { transition: box-shadow .15s, background .15s; }
        .hd-tile:hover { box-shadow: 0 6px 22px rgba(0,0,0,.08) !important; }
        .hd-row:hover { background: #fafaf9; }
        .hd-grid4 { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 16px; }
        .hd-two { display: grid; grid-template-columns: minmax(0,1.5fr) minmax(0,1fr); gap: 16px; }
        .hd-two-even { display: grid; grid-template-columns: minmax(0,1.3fr) minmax(0,1fr); gap: 16px; }
        @media (max-width: 1000px) { .hd-grid4 { grid-template-columns: repeat(2, minmax(0,1fr)); } .hd-two, .hd-two-even { grid-template-columns: 1fr; } }
        @media (max-width: 560px) { .hd-grid4 { grid-template-columns: 1fr; } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
      `}</style>

      {/* header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Performance Overview</h1>
          <p style={S.subtitle}>
            {loading
              ? "Loading your numbers…"
              : `Updated ${updatedAt ? updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {hotels.length > 1 && (
            <select value={hotelId} onChange={(e) => setHotelId(e.target.value)} style={S.select} aria-label="Filter by hotel">
              <option value="">All hotels</option>
              {hotels.map((h) => <option key={h._id} value={h._id}>{h.hotelName}</option>)}
            </select>
          )}
          <button onClick={load} style={S.ghostBtn} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
        </div>
      </div>

      {issues.length > 0 && (
        <div style={S.issue}>
          {issues.map((m, i) => <div key={i}>⚠️ {m}</div>)}
        </div>
      )}

      {loading && bookings.length === 0 && hotels.length === 0 ? (
        <div style={S.center}><div style={S.spinner} /></div>
      ) : (
        <>
          {/* needs attention */}
          {(stats.count.PENDING > 0 || stats.departures.length > 0) && (
            <div style={S.attention}>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
                {stats.count.PENDING > 0 && (
                  <span><b>{plural(stats.count.PENDING, "booking request")}</b> waiting for your reply</span>
                )}
                {stats.departures.length > 0 && (
                  <span><b>{plural(stats.departures.length, "guest")}</b> due for check-out</span>
                )}
              </div>
              <button style={S.attentionBtn} onClick={() => onNavigate(GO.bookings)}>Review bookings</button>
            </div>
          )}

          {/* top tiles */}
          <div className="hd-grid4">
            {tiles.map((t) => (
              <article key={t.title} className="hd-tile" style={S.tile}>
                <h3 style={S.tileTitle}>{t.title}</h3>
                <div style={S.tileValue}>{t.value}</div>
                <p style={S.tileSub}>{t.sub}</p>
                <button style={S.link} onClick={() => onNavigate(t.go)}>View more</button>
              </article>
            ))}
          </div>

          {/* chart + occupancy */}
          <div className="hd-two" style={{ marginTop: 16 }}>
            <section style={S.card}>
              <div style={S.cardHead}>
                <div>
                  <h3 style={S.cardTitle}>Last 7 days</h3>
                  <p style={S.cardSub}>{metric === "revenue" ? "Room revenue per night (excl. tax)" : "Rooms occupied per night"}</p>
                </div>
                <div style={S.toggle}>
                  {[["revenue", "Revenue"], ["nights", "Room nights"]].map(([k, l]) => (
                    <button key={k} onClick={() => setMetric(k)} style={{ ...S.toggleBtn, ...(metric === k ? S.toggleOn : {}) }}>{l}</button>
                  ))}
                </div>
              </div>

              <div style={S.bars}>
                {stats.days.map((d, i) => {
                  const v = chartValues[i];
                  const isToday = d === today;
                  return (
                    <div key={d} style={S.barCol} title={metric === "revenue" ? money(v) : plural(v, "room night")}>
                      <div style={S.barVal}>{v ? (metric === "revenue" ? money(v) : v) : ""}</div>
                      <div style={S.barTrack}>
                        <div style={{ ...S.bar, height: `${Math.max((v / chartMax) * 100, v ? 4 : 0)}%`, background: isToday ? "#166534" : "#86c9a0" }} />
                      </div>
                      <div style={{ ...S.barLabel, ...(isToday ? { color: "#166534", fontWeight: 700 } : {}) }}>
                        {isToday ? "Today" : weekday(d)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section style={S.card}>
              <div style={S.cardHead}>
                <div>
                  <h3 style={S.cardTitle}>Tonight's occupancy</h3>
                  <p style={S.cardSub}>Rooms with a guest tonight</p>
                </div>
              </div>
              {stats.occupancy === null ? (
                <p style={S.empty}>Add rooms to see occupancy.</p>
              ) : (
                <>
                  <div style={S.occValue}>{stats.occupancy}%</div>
                  <div style={S.occTrack}><div style={{ ...S.occFill, width: `${stats.occupancy}%` }} /></div>
                  <p style={S.cardSub}>{stats.nightsToday} of {stats.totalRooms} rooms booked</p>
                </>
              )}

              <div style={S.mini}>
                {[
                  [stats.arrivals.length, "Arrivals today"],
                  [stats.departures.length, "Check-outs due"],
                  [stats.newToday, `New bookings today${stats.newToday ? ` · ${money(stats.newTodayAmt)}` : ""}`],
                ].map(([v, l]) => (
                  <div key={l} style={S.miniRow}>
                    <span style={{ color: "#6b7280", fontSize: 14 }}>{l}</span>
                    <b style={{ fontSize: 16 }}>{v}</b>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* arrivals + setup / snapshot */}
          <div className="hd-two-even" style={{ marginTop: 16 }}>
            <section style={S.card}>
              <div style={S.cardHead}>
                <div>
                  <h3 style={S.cardTitle}>Upcoming arrivals</h3>
                  <p style={S.cardSub}>Next guests due at your hotels</p>
                </div>
                <button style={S.link} onClick={() => onNavigate(GO.bookings)}>All bookings</button>
              </div>

              {stats.upcoming.length === 0 ? (
                <p style={S.empty}>No upcoming arrivals yet.</p>
              ) : (
                <div>
                  {stats.upcoming.map((b) => {
                    const [label, c, bg] = STATUS_LABEL[b.bookingStatus] || STATUS_LABEL.CONFIRMED;
                    const d = diffDays(dayKey(b.checkIn), today);
                    return (
                      <div key={b._id} className="hd-row" style={S.arrival} onClick={() => onNavigate(GO.bookings)} role="button" tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && onNavigate(GO.bookings)}>
                        <div style={{ minWidth: 0 }}>
                          <div style={S.arrName}>{b.guest?.name || "Guest"}</div>
                          <div style={S.arrMeta}>
                            {b.hotel?.hotelName || "—"} · {b.room?.roomName || b.roomName || "Room"} × {b.roomsBooked}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={S.arrDate}>
                            {shortDate(b.checkIn)} · {b.nights}n
                          </div>
                          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 4, alignItems: "center" }}>
                            <span style={S.when}>{d === 0 ? "Today" : d === 1 ? "Tomorrow" : `in ${d} days`}</span>
                            <span style={{ ...S.badge, color: c, background: bg }}>{label}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <section style={S.card}>
                <div style={S.cardHead}>
                  <div>
                    <h3 style={S.cardTitle}>Booking status</h3>
                    <p style={S.cardSub}>All your bookings right now</p>
                  </div>
                </div>
                <div style={S.snap}>
                  {[
                    ["Pending", stats.count.PENDING, "#b45309"],
                    ["Confirmed", stats.count.CONFIRMED, "#166534"],
                    ["In house", stats.count.CHECKED_IN, "#1d4ed8"],
                    ["Cancelled", stats.count.CANCELLED, "#b91c1c"],
                  ].map(([l, v, c]) => (
                    <button key={l} style={S.snapItem} onClick={() => onNavigate(GO.bookings)}>
                      <strong style={{ color: c }}>{v}</strong>
                      <span>{l}</span>
                    </button>
                  ))}
                </div>
              </section>

              <section style={S.card}>
                <div style={S.cardHead}>
                  <div>
                    <h3 style={S.cardTitle}>Property setup</h3>
                    <p style={S.cardSub}>{setupDone} of {setup.length} steps done</p>
                  </div>
                </div>
                <div style={S.occTrack}><div style={{ ...S.occFill, width: `${(setupDone / setup.length) * 100}%` }} /></div>
                <div style={{ marginTop: 10 }}>
                  {setup.map((s) => (
                    <button key={s.label} className="hd-row" style={S.step} onClick={() => onNavigate(s.go)}>
                      <span style={{ ...S.tick, ...(s.done ? S.tickOn : {}) }}>{s.done ? "✓" : ""}</span>
                      <span style={{ flex: 1, textAlign: "left", color: s.done ? "#9ca3af" : "#374151", textDecoration: s.done ? "line-through" : "none" }}>
                        {s.label}
                      </span>
                      {s.note && <span style={S.stepNote}>{s.note}</span>}
                      <b style={{ color: "#9ca3af" }}>›</b>
                    </button>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   STYLES
============================================================ */
const S = {
  page: { maxWidth: 1200, margin: "0 auto", padding: "8px 4px 40px", fontFamily: "'DM Sans', sans-serif", color: "#111827" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 30, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: "#6b7280", marginTop: 6 },
  select: { padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "white", minWidth: 170 },
  ghostBtn: { padding: "10px 18px", background: "white", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#374151", cursor: "pointer", fontFamily: "inherit" },
  issue: { marginBottom: 16, padding: "12px 16px", background: "#fee2e2", color: "#991b1b", borderRadius: 10, fontSize: 14, lineHeight: 1.6 },
  center: { display: "flex", justifyContent: "center", alignItems: "center", height: 280 },
  spinner: { width: 40, height: 40, border: "3px solid #e5e7eb", borderTop: "3px solid #166534", borderRadius: "50%", animation: "hdspin 1s linear infinite" },

  attention: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 16, padding: "14px 18px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, fontSize: 14, color: "#92400e" },
  attentionBtn: { padding: "9px 16px", background: "#166534", color: "white", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },

  tile: { background: "white", borderRadius: 14, padding: "20px 22px", boxShadow: "0 1px 4px rgba(0,0,0,.06)", display: "flex", flexDirection: "column" },
  tileTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: "#4b5563" },
  tileValue: { fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 700, color: "#166534", margin: "10px 0 6px" },
  tileSub: { margin: 0, fontSize: 12.5, color: "#9ca3af", lineHeight: 1.5, flex: 1 },
  link: { alignSelf: "flex-start", marginTop: 12, padding: 0, background: "none", border: "none", color: "#166534", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },

  card: { background: "white", borderRadius: 14, padding: "20px 22px", boxShadow: "0 1px 4px rgba(0,0,0,.06)" },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14 },
  cardTitle: { margin: 0, fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 },
  cardSub: { margin: "4px 0 0", fontSize: 13, color: "#9ca3af" },
  empty: { color: "#9ca3af", fontSize: 14, padding: "14px 0" },

  toggle: { display: "flex", background: "#f3f4f6", borderRadius: 10, padding: 3 },
  toggleBtn: { padding: "6px 12px", background: "none", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" },
  toggleOn: { background: "white", color: "#166534", boxShadow: "0 1px 3px rgba(0,0,0,.1)" },

  bars: { display: "flex", gap: 10, alignItems: "stretch", height: 190 },
  barCol: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0 },
  barVal: { height: 16, fontSize: 11, color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" },
  barTrack: { flex: 1, width: "100%", maxWidth: 46, display: "flex", alignItems: "flex-end", background: "#f8fafc", borderRadius: 8, overflow: "hidden", margin: "4px 0" },
  bar: { width: "100%", borderRadius: 8 },
  barLabel: { fontSize: 12, color: "#9ca3af" },

  occValue: { fontFamily: "'Playfair Display', serif", fontSize: 40, fontWeight: 700, color: "#166534" },
  occTrack: { height: 8, background: "#e5e7eb", borderRadius: 8, overflow: "hidden", margin: "8px 0" },
  occFill: { height: "100%", background: "#166534", borderRadius: 8 },
  mini: { display: "grid", gap: 10, marginTop: 16, paddingTop: 14, borderTop: "1px solid #f3f4f6" },

  arrival: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 8px", borderBottom: "1px solid #f3f4f6", cursor: "pointer", borderRadius: 8 },
  arrName: { fontSize: 15, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  arrMeta: { fontSize: 12.5, color: "#9ca3af", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  arrDate: { fontSize: 14, fontWeight: 600, color: "#374151" },
  when: { fontSize: 12, color: "#6b7280" },
  badge: { fontSize: 11.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, whiteSpace: "nowrap" },

  snap: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 },
  snapItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "12px 4px", background: "#f8fafc", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: "#6b7280" },

  step: { width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 8px", background: "none", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 14 },
  tick: { width: 20, height: 20, borderRadius: "50%", border: "1.5px solid #d1d5db", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "white", flexShrink: 0 },
  tickOn: { background: "#166534", borderColor: "#166534" },
  stepNote: { fontSize: 11.5, fontWeight: 600, color: "#b45309", background: "#fef3c7", padding: "2px 8px", borderRadius: 20 },
};